// src/components/MeetCoordination.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import './MeetCoordination.css';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';

function MeetCoordination() {
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token || '';
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('team-selection');
  const [meets, setMeets] = useState([]);
  const [selectedMeetId, setSelectedMeetId] = useState('');
  const [loading, setLoading] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // Meet create/edit
  const [showMeetModal, setShowMeetModal] = useState(false);
  const [editingMeetId, setEditingMeetId] = useState(null);
  const emptyMeet = {
    event_name: '',
    event_type: 'meet',
    event_date: '',
    registration_deadline: '',
    location_details: '',
    description: '',
    cost_per_athlete: '',
    levels_allowed: '',
    max_participants: ''
  };
  const [meetForm, setMeetForm] = useState(emptyMeet);

  // Team Selection State
  const [teamSelections, setTeamSelections] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [showAddSelectionModal, setShowAddSelectionModal] = useState(false);
  const [editingSelectionId, setEditingSelectionId] = useState(null);
  const emptySelection = {
    athlete_id: '',
    events_selected: [],
    athlete_level: '',
    coach_notes: '',
    uniform_size: '',
    leo_number: '',
    session_name: '',
    report_time: '',
    compete_time: ''
  };
  const [newSelection, setNewSelection] = useState(emptySelection);

  // Push-to-calendar
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushing, setPushing] = useState(false);

  // Travel Plans State
  const [travelPlans, setTravelPlans] = useState([]);
  const [showAddTravelModal, setShowAddTravelModal] = useState(false);
  const emptyTravel = {
    travel_type: 'bus',
    departure_datetime: '',
    return_datetime: '',
    departure_location: '',
    hotel_name: '',
    hotel_address: '',
    hotel_phone: '',
    hotel_confirmation: '',
    cost_per_person: '',
    notes: ''
  };
  const [newTravel, setNewTravel] = useState(emptyTravel);

  // Checklists State
  const [checklists, setChecklists] = useState([]);
  const [showAddChecklistModal, setShowAddChecklistModal] = useState(false);
  const emptyChecklist = {
    item_name: '',
    assigned_to_role: 'coach',
    due_date: '',
    priority: 'medium'
  };
  const [newChecklist, setNewChecklist] = useState(emptyChecklist);

  // Results State
  const [results, setResults] = useState([]);
  const [showAddResultModal, setShowAddResultModal] = useState(false);
  const emptyResult = {
    athlete_id: '',
    event_name: 'vault',
    score: '',
    place: '',
    awarded_medal: 'none',
    personal_best: false,
    coach_feedback: ''
  };
  const [newResult, setNewResult] = useState(emptyResult);

  // Load meets on component mount
  useEffect(() => {
    loadMeets();
    loadAthletes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select meet from nav state
  useEffect(() => {
    if (location.state?.meetId && meets.length > 0) {
      setSelectedMeetId(location.state.meetId);
    }
  }, [location.state, meets]);

  // Load data when meet selection changes
  useEffect(() => {
    if (selectedMeetId) {
      if (activeTab === 'team-selection') loadTeamSelections();
      if (activeTab === 'travel') loadTravelPlans();
      if (activeTab === 'checklists') loadChecklists();
      if (activeTab === 'results') loadResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeetId, activeTab]);

  const loadMeets = async (selectId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/competition-events`, authHeader);
      const meetsData = Array.isArray(response.data) ? response.data : [];
      setMeets(meetsData);
      if (selectId) {
        setSelectedMeetId(selectId);
      } else if (meetsData.length > 0 && !selectedMeetId) {
        setSelectedMeetId(meetsData[0].id);
      }
    } catch (error) {
      console.error('Error loading meets:', error);
    }
  };

  const loadAthletes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/athletes`, authHeader);
      setAthletes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading athletes:', error);
    }
  };

  // ========================================
  // MEET CREATE / EDIT / DELETE
  // ========================================

  const openCreateMeet = () => {
    setEditingMeetId(null);
    setMeetForm(emptyMeet);
    setShowMeetModal(true);
  };

  const openEditMeet = () => {
    const meet = meets.find(m => String(m.id) === String(selectedMeetId));
    if (!meet) return;
    setEditingMeetId(meet.id);
    let levels = '';
    try {
      const parsed = typeof meet.levels_allowed === 'string'
        ? JSON.parse(meet.levels_allowed || '[]') : (meet.levels_allowed || []);
      levels = Array.isArray(parsed) ? parsed.join(', ') : '';
    } catch (e) { levels = ''; }
    setMeetForm({
      event_name: meet.event_name || '',
      event_type: meet.event_type || 'meet',
      event_date: meet.event_date ? String(meet.event_date).slice(0, 10) : '',
      registration_deadline: meet.registration_deadline ? String(meet.registration_deadline).slice(0, 10) : '',
      location_details: meet.location_details || '',
      description: meet.description || '',
      cost_per_athlete: meet.cost_per_athlete ?? '',
      levels_allowed: levels,
      max_participants: meet.max_participants ?? ''
    });
    setShowMeetModal(true);
  };

  const handleSaveMeet = async () => {
    if (!meetForm.event_name || !meetForm.event_date) {
      toast.error('Meet name and date are required');
      return;
    }
    const payload = {
      ...meetForm,
      cost_per_athlete: meetForm.cost_per_athlete === '' ? null : meetForm.cost_per_athlete,
      max_participants: meetForm.max_participants === '' ? null : meetForm.max_participants,
      levels_allowed: meetForm.levels_allowed
        ? meetForm.levels_allowed.split(',').map(s => s.trim()).filter(Boolean)
        : null
    };
    try {
      if (editingMeetId) {
        await axios.put(`${API_BASE_URL}/api/competition-events/${editingMeetId}`, payload, authHeader);
        setShowMeetModal(false);
        loadMeets(editingMeetId);
        toast.success('Meet updated');
      } else {
        const res = await axios.post(`${API_BASE_URL}/api/competition-events`, payload, authHeader);
        setShowMeetModal(false);
        loadMeets(res.data?.id);
        toast.success('Meet created');
      }
    } catch (error) {
      console.error('Error saving meet:', error);
      toast.error(error.response?.data?.error || 'Failed to save meet');
    }
  };

  const handleDeleteMeet = async () => {
    if (!selectedMeetId) return;
    if (!window.confirm('Delete this meet? This also removes events it pushed to athlete calendars.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/competition-events/${selectedMeetId}`, authHeader);
      setSelectedMeetId('');
      loadMeets();
      toast.success('Meet deleted');
    } catch (error) {
      console.error('Error deleting meet:', error);
      toast.error(error.response?.data?.error || 'Failed to delete meet');
    }
  };

  const selectedMeet = meets.find(m => String(m.id) === String(selectedMeetId));

  // ========================================
  // TEAM SELECTION FUNCTIONS
  // ========================================

  const loadTeamSelections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/meets/${selectedMeetId}/team-selections`, authHeader);
      setTeamSelections(response.data || []);
    } catch (error) {
      console.error('Error loading team selections:', error);
      setTeamSelections([]);
    } finally {
      setLoading(false);
    }
  };

  const openAddSelection = () => {
    setEditingSelectionId(null);
    setNewSelection(emptySelection);
    setShowAddSelectionModal(true);
  };

  const openEditSelection = (selection) => {
    setEditingSelectionId(selection.id);
    let events = [];
    try { events = JSON.parse(selection.events_selected || '[]'); } catch (e) { events = []; }
    setNewSelection({
      athlete_id: selection.athlete_id,
      events_selected: events,
      athlete_level: selection.athlete_level || '',
      coach_notes: selection.coach_notes || '',
      uniform_size: selection.uniform_size || '',
      leo_number: selection.leo_number || '',
      session_name: selection.session_name || '',
      report_time: selection.report_time ? String(selection.report_time).slice(0, 5) : '',
      compete_time: selection.compete_time ? String(selection.compete_time).slice(0, 5) : ''
    });
    setShowAddSelectionModal(true);
  };

  const handleAddSelection = async () => {
    if (!newSelection.athlete_id || newSelection.events_selected.length === 0) {
      toast.error('Please select athlete and at least one event');
      return;
    }
    try {
      if (editingSelectionId) {
        await axios.put(
          `${API_BASE_URL}/api/meets/${selectedMeetId}/team-selections/${editingSelectionId}`,
          newSelection, authHeader);
      } else {
        await axios.post(
          `${API_BASE_URL}/api/meets/${selectedMeetId}/team-selections`,
          newSelection, authHeader);
      }
      setShowAddSelectionModal(false);
      setEditingSelectionId(null);
      setNewSelection(emptySelection);
      loadTeamSelections();
    } catch (error) {
      console.error('Error saving selection:', error);
      toast.error(error.response?.data?.error || 'Failed to save selection');
    }
  };

  const toggleEvent = (event) => {
    setNewSelection(prev => ({
      ...prev,
      events_selected: prev.events_selected.includes(event)
        ? prev.events_selected.filter(e => e !== event)
        : [...prev.events_selected, event]
    }));
  };

  const activeSelections = teamSelections.filter(s => s.selection_status !== 'scratched');

  const handlePushToCalendar = async () => {
    setPushing(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/meets/${selectedMeetId}/push-to-calendar`, {}, authHeader);
      setShowPushModal(false);
      toast.success(res.data?.message || `Pushed to ${res.data?.pushed || 0} athlete calendars`);
      loadTeamSelections();
    } catch (error) {
      console.error('Error pushing to calendar:', error);
      toast.error(error.response?.data?.error || 'Failed to push to calendars');
    } finally {
      setPushing(false);
    }
  };

  // ========================================
  // TRAVEL PLANS FUNCTIONS
  // ========================================

  const loadTravelPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/meets/${selectedMeetId}/travel-plans`, authHeader);
      setTravelPlans(response.data || []);
    } catch (error) {
      console.error('Error loading travel plans:', error);
      setTravelPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTravel = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/meets/${selectedMeetId}/travel-plans`, newTravel, authHeader);
      setShowAddTravelModal(false);
      setNewTravel(emptyTravel);
      loadTravelPlans();
    } catch (error) {
      console.error('Error adding travel plan:', error);
      toast.error(error.response?.data?.error || 'Failed to add travel plan');
    }
  };

  // ========================================
  // CHECKLISTS FUNCTIONS
  // ========================================

  const loadChecklists = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/meets/${selectedMeetId}/checklists`, authHeader);
      setChecklists(response.data || []);
    } catch (error) {
      console.error('Error loading checklists:', error);
      setChecklists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddChecklist = async () => {
    if (!newChecklist.item_name) {
      toast.error('Please enter a task');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/meets/${selectedMeetId}/checklists`, newChecklist, authHeader);
      setShowAddChecklistModal(false);
      setNewChecklist(emptyChecklist);
      loadChecklists();
    } catch (error) {
      console.error('Error adding checklist:', error);
      toast.error(error.response?.data?.error || 'Failed to add checklist item');
    }
  };

  const toggleChecklistComplete = async (checklistId, currentStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/api/checklists/${checklistId}/complete`,
        { is_completed: !currentStatus }, authHeader);
      loadChecklists();
    } catch (error) {
      console.error('Error updating checklist:', error);
    }
  };

  // ========================================
  // RESULTS FUNCTIONS
  // ========================================

  const loadResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/meets/${selectedMeetId}/results`, authHeader);
      setResults(response.data || []);
    } catch (error) {
      console.error('Error loading results:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddResult = async () => {
    if (!newResult.athlete_id || !newResult.score) {
      toast.error('Please select athlete and enter score');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/meets/${selectedMeetId}/results`, newResult, authHeader);
      setShowAddResultModal(false);
      setNewResult(emptyResult);
      loadResults();
    } catch (error) {
      console.error('Error adding result:', error);
      toast.error(error.response?.data?.error || 'Failed to add result');
    }
  };

  const getMedalIcon = (medal) => {
    if (medal === 'gold') return '🥇';
    if (medal === 'silver') return '🥈';
    if (medal === 'bronze') return '🥉';
    return '';
  };

  const fmtTime = (t) => (t ? String(t).slice(0, 5) : '');

  // ========================================
  // RENDER
  // ========================================

  return (
    <div className="meet-coordination-container">
      <div className="meet-header">
        <h1>🏆 Meet Coordination</h1>
        <p>Create meets, assign session times, coordinate travel — then push it to athlete calendars</p>
      </div>

      {/* Meet Selector + management */}
      <div className="meet-selector">
        <label>Select Meet:</label>
        <select
          value={selectedMeetId}
          onChange={(e) => setSelectedMeetId(e.target.value)}
        >
          <option value="">-- Select a Meet --</option>
          {meets.map(meet => (
            <option key={meet.id} value={meet.id}>
              {meet.event_name} - {new Date(meet.event_date).toLocaleDateString()}
            </option>
          ))}
        </select>
        <button className="btn-primary" onClick={openCreateMeet}>＋ New Meet</button>
        {selectedMeetId && (
          <>
            <button className="btn-secondary" onClick={openEditMeet}>Edit</button>
            <button className="btn-cancel" onClick={handleDeleteMeet}>Delete</button>
          </>
        )}
      </div>

      {selectedMeet && (
        <div className="meet-summary">
          <span>📅 {new Date(selectedMeet.event_date).toLocaleDateString()}</span>
          {selectedMeet.location_details && <span>📍 {selectedMeet.location_details}</span>}
          {selectedMeet.registration_deadline && (
            <span>📝 Register by {new Date(selectedMeet.registration_deadline).toLocaleDateString()}</span>
          )}
          {selectedMeet.cost_per_athlete != null && Number(selectedMeet.cost_per_athlete) > 0 && (
            <span>💲 ${Number(selectedMeet.cost_per_athlete).toFixed(2)}/athlete</span>
          )}
        </div>
      )}

      {!selectedMeetId ? (
        <div className="empty-state">
          <p>Select a meet to manage coordination, or create your first meet.</p>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="meet-tabs">
            <button className={activeTab === 'team-selection' ? 'tab-active' : ''} onClick={() => setActiveTab('team-selection')}>Team Selection</button>
            <button className={activeTab === 'travel' ? 'tab-active' : ''} onClick={() => setActiveTab('travel')}>Travel &amp; Logistics</button>
            <button className={activeTab === 'checklists' ? 'tab-active' : ''} onClick={() => setActiveTab('checklists')}>Checklists</button>
            <button className={activeTab === 'results' ? 'tab-active' : ''} onClick={() => setActiveTab('results')}>Results</button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">

            {/* TEAM SELECTION TAB */}
            {activeTab === 'team-selection' && (
              <div>
                <div className="tab-header">
                  <h2>Team Selection &amp; Session Times</h2>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {activeSelections.length > 0 && (
                      <button className="btn-primary" onClick={() => setShowPushModal(true)}>
                        📅 Push schedule to athlete calendars
                      </button>
                    )}
                    <button className="btn-primary" onClick={openAddSelection}>+ Add Athlete</button>
                  </div>
                </div>

                {loading ? (
                  <p>Loading...</p>
                ) : teamSelections.length === 0 ? (
                  <p className="empty-state">No athletes selected yet. Start building your team!</p>
                ) : (
                  <div className="selections-grid">
                    {teamSelections.map(selection => {
                      let events = [];
                      try { events = JSON.parse(selection.events_selected || '[]'); } catch (e) { events = []; }
                      return (
                        <div key={selection.id} className="selection-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3>{selection.athlete_name}</h3>
                            <button className="btn-secondary" onClick={() => openEditSelection(selection)}>Edit</button>
                          </div>
                          <div className="events-badges">
                            {events.map(event => (
                              <span key={event} className="event-badge">{event}</span>
                            ))}
                          </div>
                          {selection.session_name && <p><strong>Session:</strong> {selection.session_name}</p>}
                          {selection.report_time && <p><strong>Report:</strong> {fmtTime(selection.report_time)}</p>}
                          {selection.compete_time && <p><strong>Compete:</strong> {fmtTime(selection.compete_time)}</p>}
                          {selection.athlete_level && <p><strong>Level:</strong> {selection.athlete_level}</p>}
                          {selection.uniform_size && <p><strong>Uniform:</strong> {selection.uniform_size}</p>}
                          {selection.leo_number && <p><strong>Leo #:</strong> {selection.leo_number}</p>}
                          {selection.coach_notes && <p className="notes">{selection.coach_notes}</p>}
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <span className={`status-badge status-${selection.selection_status}`}>
                              {selection.selection_status}
                            </span>
                            {selection.pushed_to_calendar ? (
                              <span className="status-badge" style={{ background: '#4CAF50', color: '#fff' }}>✓ on calendar</span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add/Edit Selection Modal */}
                {showAddSelectionModal && (
                  <div className="modal-overlay" onClick={() => setShowAddSelectionModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h2>{editingSelectionId ? 'Edit Athlete' : 'Add Athlete to Team'}</h2>

                      <label>Athlete:</label>
                      <select
                        value={newSelection.athlete_id}
                        onChange={(e) => setNewSelection({ ...newSelection, athlete_id: e.target.value })}
                        disabled={!!editingSelectionId}
                      >
                        <option value="">-- Select Athlete --</option>
                        {athletes.map(athlete => (
                          <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                        ))}
                      </select>

                      <label>Events:</label>
                      <div className="event-checkboxes">
                        {['vault', 'bars', 'beam', 'floor'].map(event => (
                          <label key={event}>
                            <input
                              type="checkbox"
                              checked={newSelection.events_selected.includes(event)}
                              onChange={() => toggleEvent(event)}
                            />
                            {event.charAt(0).toUpperCase() + event.slice(1)}
                          </label>
                        ))}
                      </div>

                      <div className="form-row" style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <label>Report Time:</label>
                          <input
                            type="time"
                            value={newSelection.report_time}
                            onChange={(e) => setNewSelection({ ...newSelection, report_time: e.target.value })}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label>Compete Time:</label>
                          <input
                            type="time"
                            value={newSelection.compete_time}
                            onChange={(e) => setNewSelection({ ...newSelection, compete_time: e.target.value })}
                          />
                        </div>
                      </div>

                      <label>Session (optional):</label>
                      <input
                        type="text"
                        value={newSelection.session_name}
                        onChange={(e) => setNewSelection({ ...newSelection, session_name: e.target.value })}
                        placeholder="e.g., Session 2, AM squad"
                      />

                      <label>Level:</label>
                      <input
                        type="text"
                        value={newSelection.athlete_level}
                        onChange={(e) => setNewSelection({ ...newSelection, athlete_level: e.target.value })}
                        placeholder="e.g., Level 7, Xcel Gold"
                      />

                      <label>Uniform Size:</label>
                      <input
                        type="text"
                        value={newSelection.uniform_size}
                        onChange={(e) => setNewSelection({ ...newSelection, uniform_size: e.target.value })}
                        placeholder="e.g., Adult Small"
                      />

                      <label>Leo Number:</label>
                      <input
                        type="text"
                        value={newSelection.leo_number}
                        onChange={(e) => setNewSelection({ ...newSelection, leo_number: e.target.value })}
                        placeholder="e.g., 42"
                      />

                      <label>Coach Notes:</label>
                      <textarea
                        value={newSelection.coach_notes}
                        onChange={(e) => setNewSelection({ ...newSelection, coach_notes: e.target.value })}
                        placeholder="Any special notes..."
                        rows="3"
                      />

                      <div className="modal-actions">
                        <button className="btn-cancel" onClick={() => setShowAddSelectionModal(false)}>Cancel</button>
                        <button className="btn-primary" onClick={handleAddSelection}>
                          {editingSelectionId ? 'Save Changes' : 'Add to Team'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Push confirm modal */}
                {showPushModal && (
                  <div className="modal-overlay" onClick={() => setShowPushModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h2>Push schedule to calendars</h2>
                      <p>
                        This adds a meet event to the calendars of these athletes (and their linked
                        parents), with each athlete's report/compete time, events, and travel info.
                        Re-pushing updates their existing event.
                      </p>
                      <ul style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {activeSelections.map(s => (
                          <li key={s.id}>
                            {s.athlete_name}
                            {s.report_time ? ` — report ${fmtTime(s.report_time)}` : ' — no report time set'}
                          </li>
                        ))}
                      </ul>
                      <div className="modal-actions">
                        <button className="btn-cancel" onClick={() => setShowPushModal(false)}>Cancel</button>
                        <button className="btn-primary" onClick={handlePushToCalendar} disabled={pushing}>
                          {pushing ? 'Pushing…' : `Push to ${activeSelections.length} athlete(s)`}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TRAVEL PLANS TAB */}
            {activeTab === 'travel' && (
              <div>
                <div className="tab-header">
                  <h2>Travel &amp; Logistics</h2>
                  <button className="btn-primary" onClick={() => setShowAddTravelModal(true)}>+ Add Travel Plan</button>
                </div>

                {loading ? (
                  <p>Loading...</p>
                ) : travelPlans.length === 0 ? (
                  <p className="empty-state">No travel plans yet. Add hotel and transportation recommendations.</p>
                ) : (
                  <div className="travel-list">
                    {travelPlans.map(plan => (
                      <div key={plan.id} className="travel-card">
                        <h3>{(plan.travel_type || '').toUpperCase()}</h3>
                        {plan.hotel_name && (
                          <div className="hotel-info">
                            <p><strong>Hotel:</strong> {plan.hotel_name}</p>
                            {plan.hotel_address && <p><strong>Address:</strong> {plan.hotel_address}</p>}
                            {plan.hotel_phone && <p><strong>Phone:</strong> {plan.hotel_phone}</p>}
                            {plan.hotel_confirmation && <p><strong>Confirmation:</strong> {plan.hotel_confirmation}</p>}
                          </div>
                        )}
                        {plan.departure_location && <p><strong>Depart from:</strong> {plan.departure_location}</p>}
                        {plan.departure_datetime && (
                          <p><strong>Departure:</strong> {new Date(plan.departure_datetime).toLocaleString()}</p>
                        )}
                        {plan.return_datetime && (
                          <p><strong>Return:</strong> {new Date(plan.return_datetime).toLocaleString()}</p>
                        )}
                        {plan.cost_per_person != null && Number(plan.cost_per_person) > 0 && (
                          <p className="cost"><strong>Cost:</strong> ${parseFloat(plan.cost_per_person).toFixed(2)}/person</p>
                        )}
                        {plan.notes && <p className="notes">{plan.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Travel Modal */}
                {showAddTravelModal && (
                  <div className="modal-overlay" onClick={() => setShowAddTravelModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h2>Add Travel Plan</h2>

                      <label>Travel Type:</label>
                      <select
                        value={newTravel.travel_type}
                        onChange={(e) => setNewTravel({ ...newTravel, travel_type: e.target.value })}
                      >
                        <option value="bus">Bus</option>
                        <option value="van">Van</option>
                        <option value="carpool">Carpool</option>
                        <option value="hotel">Hotel</option>
                        <option value="flight">Flight</option>
                      </select>

                      <label>Depart From:</label>
                      <input
                        type="text"
                        value={newTravel.departure_location}
                        onChange={(e) => setNewTravel({ ...newTravel, departure_location: e.target.value })}
                        placeholder="e.g., Gym parking lot"
                      />

                      <label>Hotel Name:</label>
                      <input
                        type="text"
                        value={newTravel.hotel_name}
                        onChange={(e) => setNewTravel({ ...newTravel, hotel_name: e.target.value })}
                      />

                      <label>Hotel Address:</label>
                      <input
                        type="text"
                        value={newTravel.hotel_address}
                        onChange={(e) => setNewTravel({ ...newTravel, hotel_address: e.target.value })}
                      />

                      <label>Hotel Phone:</label>
                      <input
                        type="tel"
                        value={newTravel.hotel_phone}
                        onChange={(e) => setNewTravel({ ...newTravel, hotel_phone: e.target.value })}
                      />

                      <label>Confirmation Number:</label>
                      <input
                        type="text"
                        value={newTravel.hotel_confirmation}
                        onChange={(e) => setNewTravel({ ...newTravel, hotel_confirmation: e.target.value })}
                      />

                      <label>Departure:</label>
                      <input
                        type="datetime-local"
                        value={newTravel.departure_datetime}
                        onChange={(e) => setNewTravel({ ...newTravel, departure_datetime: e.target.value })}
                      />

                      <label>Return:</label>
                      <input
                        type="datetime-local"
                        value={newTravel.return_datetime}
                        onChange={(e) => setNewTravel({ ...newTravel, return_datetime: e.target.value })}
                      />

                      <label>Cost Per Person:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newTravel.cost_per_person}
                        onChange={(e) => setNewTravel({ ...newTravel, cost_per_person: e.target.value })}
                      />

                      <label>Notes:</label>
                      <textarea
                        value={newTravel.notes}
                        onChange={(e) => setNewTravel({ ...newTravel, notes: e.target.value })}
                        rows="3"
                      />

                      <div className="modal-actions">
                        <button className="btn-cancel" onClick={() => setShowAddTravelModal(false)}>Cancel</button>
                        <button className="btn-primary" onClick={handleAddTravel}>Add Travel Plan</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CHECKLISTS TAB */}
            {activeTab === 'checklists' && (
              <div>
                <div className="tab-header">
                  <h2>Pre-Meet Checklist</h2>
                  <button className="btn-primary" onClick={() => setShowAddChecklistModal(true)}>+ Add Task</button>
                </div>

                {loading ? (
                  <p>Loading...</p>
                ) : checklists.length === 0 ? (
                  <p className="empty-state">No checklist items yet. Add tasks to prepare for the meet.</p>
                ) : (
                  <div className="checklist-items">
                    {checklists.map(item => (
                      <div key={item.id} className={`checklist-item ${item.completed ? 'completed' : ''}`}>
                        <input
                          type="checkbox"
                          checked={!!item.completed}
                          onChange={() => toggleChecklistComplete(item.id, item.completed)}
                        />
                        <div className="item-details">
                          <p className="task-description">{item.item_name}</p>
                          <div className="item-meta">
                            {item.priority && (
                              <span className={`priority-badge priority-${item.priority}`}>{item.priority}</span>
                            )}
                            {item.due_date && (
                              <span className="due-date">Due: {new Date(item.due_date).toLocaleDateString()}</span>
                            )}
                            {item.assigned_to_role && (
                              <span className="assigned-to">Assigned: {item.assigned_to_role}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Checklist Modal */}
                {showAddChecklistModal && (
                  <div className="modal-overlay" onClick={() => setShowAddChecklistModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h2>Add Checklist Item</h2>

                      <label>Task:</label>
                      <textarea
                        value={newChecklist.item_name}
                        onChange={(e) => setNewChecklist({ ...newChecklist, item_name: e.target.value })}
                        placeholder="e.g., Pack floor music USB, Print team roster"
                        rows="3"
                      />

                      <label>Assign To:</label>
                      <select
                        value={newChecklist.assigned_to_role}
                        onChange={(e) => setNewChecklist({ ...newChecklist, assigned_to_role: e.target.value })}
                      >
                        <option value="coach">Coach</option>
                        <option value="parent">Parent</option>
                        <option value="admin">Admin</option>
                      </select>

                      <label>Priority:</label>
                      <select
                        value={newChecklist.priority}
                        onChange={(e) => setNewChecklist({ ...newChecklist, priority: e.target.value })}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>

                      <label>Due Date:</label>
                      <input
                        type="date"
                        value={newChecklist.due_date}
                        onChange={(e) => setNewChecklist({ ...newChecklist, due_date: e.target.value })}
                      />

                      <div className="modal-actions">
                        <button className="btn-cancel" onClick={() => setShowAddChecklistModal(false)}>Cancel</button>
                        <button className="btn-primary" onClick={handleAddChecklist}>Add Task</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* RESULTS TAB */}
            {activeTab === 'results' && (
              <div>
                <div className="tab-header">
                  <h2>Competition Results</h2>
                  <button className="btn-primary" onClick={() => setShowAddResultModal(true)}>+ Add Result</button>
                </div>

                {loading ? (
                  <p>Loading...</p>
                ) : results.length === 0 ? (
                  <p className="empty-state">No results entered yet. Add scores after the competition.</p>
                ) : (
                  <div className="results-grid">
                    {results.map(result => (
                      <div key={result.id} className="result-card">
                        <h3>{result.athlete_name}</h3>
                        <p className="event-name">{(result.event_name || '').toUpperCase()}</p>
                        <div className="score-display">
                          <span className="score">{parseFloat(result.score).toFixed(3)}</span>
                          {result.awarded_medal && result.awarded_medal !== 'none' && (
                            <span className="medal-icon">{getMedalIcon(result.awarded_medal)}</span>
                          )}
                        </div>
                        {result.place && (
                          <p className="placing">{result.place}{getPlacingSuffix(result.place)} Place</p>
                        )}
                        {result.personal_best ? (
                          <span className="pb-badge">🎉 Personal Best!</span>
                        ) : null}
                        {result.coach_feedback && <p className="notes">{result.coach_feedback}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Result Modal */}
                {showAddResultModal && (
                  <div className="modal-overlay" onClick={() => setShowAddResultModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <h2>Add Competition Result</h2>

                      <label>Athlete:</label>
                      <select
                        value={newResult.athlete_id}
                        onChange={(e) => setNewResult({ ...newResult, athlete_id: e.target.value })}
                      >
                        <option value="">-- Select Athlete --</option>
                        {athletes.map(athlete => (
                          <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                        ))}
                      </select>

                      <label>Event:</label>
                      <select
                        value={newResult.event_name}
                        onChange={(e) => setNewResult({ ...newResult, event_name: e.target.value })}
                      >
                        <option value="vault">Vault</option>
                        <option value="bars">Bars</option>
                        <option value="beam">Beam</option>
                        <option value="floor">Floor</option>
                        <option value="all_around">All Around</option>
                      </select>

                      <label>Score:</label>
                      <input
                        type="number"
                        step="0.001"
                        value={newResult.score}
                        onChange={(e) => setNewResult({ ...newResult, score: e.target.value })}
                        placeholder="9.525"
                      />

                      <label>Place:</label>
                      <input
                        type="number"
                        value={newResult.place}
                        onChange={(e) => setNewResult({ ...newResult, place: e.target.value })}
                        placeholder="1"
                      />

                      <label>Medal:</label>
                      <select
                        value={newResult.awarded_medal}
                        onChange={(e) => setNewResult({ ...newResult, awarded_medal: e.target.value })}
                      >
                        <option value="none">None</option>
                        <option value="gold">🥇 Gold</option>
                        <option value="silver">🥈 Silver</option>
                        <option value="bronze">🥉 Bronze</option>
                      </select>

                      <label>
                        <input
                          type="checkbox"
                          checked={newResult.personal_best}
                          onChange={(e) => setNewResult({ ...newResult, personal_best: e.target.checked })}
                        />
                        Personal Best
                      </label>

                      <label>Coach Feedback:</label>
                      <textarea
                        value={newResult.coach_feedback}
                        onChange={(e) => setNewResult({ ...newResult, coach_feedback: e.target.value })}
                        rows="2"
                      />

                      <div className="modal-actions">
                        <button className="btn-cancel" onClick={() => setShowAddResultModal(false)}>Cancel</button>
                        <button className="btn-primary" onClick={handleAddResult}>Add Result</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </>
      )}

      {/* Create/Edit Meet Modal */}
      {showMeetModal && (
        <div className="modal-overlay" onClick={() => setShowMeetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingMeetId ? 'Edit Meet' : 'New Meet'}</h2>

            <label>Meet Name:</label>
            <input
              type="text"
              value={meetForm.event_name}
              onChange={(e) => setMeetForm({ ...meetForm, event_name: e.target.value })}
              placeholder="e.g., State Championships"
            />

            <div className="form-row" style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label>Type:</label>
                <select
                  value={meetForm.event_type}
                  onChange={(e) => setMeetForm({ ...meetForm, event_type: e.target.value })}
                >
                  <option value="meet">Meet</option>
                  <option value="competition">Competition</option>
                  <option value="invitational">Invitational</option>
                  <option value="championship">Championship</option>
                  <option value="showcase">Showcase</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label>Date:</label>
                <input
                  type="date"
                  value={meetForm.event_date}
                  onChange={(e) => setMeetForm({ ...meetForm, event_date: e.target.value })}
                />
              </div>
            </div>

            <label>Registration Deadline:</label>
            <input
              type="date"
              value={meetForm.registration_deadline}
              onChange={(e) => setMeetForm({ ...meetForm, registration_deadline: e.target.value })}
            />

            <label>Location / Venue:</label>
            <input
              type="text"
              value={meetForm.location_details}
              onChange={(e) => setMeetForm({ ...meetForm, location_details: e.target.value })}
              placeholder="Venue name + address"
            />

            <div className="form-row" style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label>Cost / Athlete:</label>
                <input
                  type="number"
                  step="0.01"
                  value={meetForm.cost_per_athlete}
                  onChange={(e) => setMeetForm({ ...meetForm, cost_per_athlete: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>Max Participants:</label>
                <input
                  type="number"
                  value={meetForm.max_participants}
                  onChange={(e) => setMeetForm({ ...meetForm, max_participants: e.target.value })}
                />
              </div>
            </div>

            <label>Levels Allowed (comma separated):</label>
            <input
              type="text"
              value={meetForm.levels_allowed}
              onChange={(e) => setMeetForm({ ...meetForm, levels_allowed: e.target.value })}
              placeholder="e.g., 6, 7, 8, Xcel Gold"
            />

            <label>Description / Notes:</label>
            <textarea
              value={meetForm.description}
              onChange={(e) => setMeetForm({ ...meetForm, description: e.target.value })}
              rows="3"
              placeholder="General logistics, schedule notes, etc."
            />

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowMeetModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveMeet}>
                {editingMeetId ? 'Save Changes' : 'Create Meet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for placing suffix
function getPlacingSuffix(placing) {
  const j = placing % 10;
  const k = placing % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

export default MeetCoordination;
